import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { settingService } from '../services/resources';
import { FORM_ROUTES, groupLabel } from '../config/forms';
import ChangePasswordModal from './ChangePasswordModal';

// --- tiny inline icons (no icon library) ---
const Icon = {
  Menu: (p) => (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" {...p}>
      <path d="M3 6h18M3 12h18M3 18h18" strokeLinecap="round" />
    </svg>
  ),
  Chevron: (p) => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" {...p}>
      <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Bell: (p) => (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" {...p}>
      <path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M13.7 21a2 2 0 01-3.4 0" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};

const OPEN_GROUPS_KEY = 'bcaex_open_groups';

/**
 * Builds ordered sidebar sections from the user's permission set.
 * A section is either a collapsible group (ParentGroup) or a standalone item.
 */
function useSidebarSections() {
  const { permissions } = useAuth();
  return useMemo(() => {
    const viewable = permissions.filter((p) => p.CanView && FORM_ROUTES[p.FormCode]);
    const groups = {};
    const standalone = [];
    viewable.forEach((p) => {
      if (p.ParentGroup) {
        (groups[p.ParentGroup] = groups[p.ParentGroup] || []).push(p);
      } else {
        standalone.push(p);
      }
    });

    const sections = [];
    Object.entries(groups).forEach(([key, items]) => {
      items.sort((a, b) => a.SortOrder - b.SortOrder);
      sections.push({ type: 'group', key, label: groupLabel(key), items, order: items[0].SortOrder });
    });
    standalone.forEach((p) => sections.push({ type: 'item', ...p, order: p.SortOrder }));
    sections.sort((a, b) => a.order - b.order);
    return sections;
  }, [permissions]);
}

export default function Layout({ onToast }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const sections = useSidebarSections();

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [changePwOpen, setChangePwOpen] = useState(false);
  const [storeName, setStoreName] = useState('CAM EX');
  const menuRef = useRef();

  const [openGroups, setOpenGroups] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(OPEN_GROUPS_KEY)) || {};
    } catch {
      return {};
    }
  });

  // Best-effort store name for the brand area (ignored if user lacks permission).
  useEffect(() => {
    settingService
      .get()
      .then((d) => d.StoreName && setStoreName(d.StoreName))
      .catch(() => {});
  }, []);

  // Close the user menu on outside click.
  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const isGroupOpen = (key) => openGroups[key] === true; // default collapsed
  const toggleGroup = (key) => {
    setOpenGroups((prev) => {
      const next = prev[key] ? {} : { [key]: true };
      localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify(next));
      return next;
    });
  };

  const onLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const linkClass = ({ isActive }) =>
    `block rounded-md px-3 py-2 text-sm ${
      isActive ? 'bg-white/20 font-medium' : 'text-white/80 hover:bg-white/10'
    }`;

  return (
    <div className="flex min-h-screen flex-col">
      {/* Top bar */}
      <header className="flex h-14 items-center justify-between border-b border-gray-200 bg-white px-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen((v) => !v)}
            className="rounded-md p-1.5 text-gray-600 hover:bg-gray-100"
            aria-label="Toggle sidebar"
          >
            <Icon.Menu />
          </button>
          <span className="text-lg font-bold text-brand">{storeName}</span>
        </div>

        <div className="flex items-center gap-3">
          <button className="relative rounded-md p-1.5 text-gray-500 hover:bg-gray-100" aria-label="Notifications">
            <Icon.Bell />
            <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-red-500" />
          </button>

          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setUserMenuOpen((v) => !v)}
              className="flex items-center gap-2 rounded-md px-2 py-1 text-sm hover:bg-gray-100"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand text-xs font-bold text-white">
                {(user?.username || '?').slice(0, 2).toUpperCase()}
              </span>
              <span className="text-left leading-tight">
                <span className="block font-medium text-gray-800">{user?.username}</span>
                <span className="block text-xs text-gray-400">{user?.groupName}</span>
              </span>
            </button>
            {userMenuOpen && (
              <div className="absolute right-0 mt-1 w-44 rounded-md border border-gray-200 bg-white py-1 shadow-lg">
                <button
                  onClick={() => {
                    setUserMenuOpen(false);
                    setChangePwOpen(true);
                  }}
                  className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                >
                  Change Password
                </button>
                <button
                  onClick={onLogout}
                  className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                >
                  Log out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        {sidebarOpen && (
          <aside className="w-60 shrink-0 overflow-y-auto bg-brand-dark py-3 text-white">
            <nav className="space-y-1 px-3">
              {sections.map((section) =>
                section.type === 'group' ? (
                  <div key={section.key}>
                    <button
                      onClick={() => toggleGroup(section.key)}
                      className="flex w-full items-center justify-between rounded-md px-3 py-2 text-xs font-semibold uppercase tracking-wide text-white/60 hover:bg-white/5"
                    >
                      {section.label}
                      <Icon.Chevron
                        style={{ transform: isGroupOpen(section.key) ? 'none' : 'rotate(-90deg)' }}
                      />
                    </button>
                    {isGroupOpen(section.key) && (
                      <div className="mt-0.5 space-y-0.5 pl-2">
                        {section.items.map((item) => (
                          <NavLink key={item.FormCode} to={FORM_ROUTES[item.FormCode]} className={linkClass}>
                            {item.FormName}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <NavLink key={section.FormCode} to={FORM_ROUTES[section.FormCode]} className={linkClass}>
                    {section.FormName}
                  </NavLink>
                )
              )}
            </nav>
          </aside>
        )}

        {/* min-w-0 overrides the flex item's default min-width:auto — without
            it, a wide table's content forces this column wider than the
            available space instead of triggering its own scroll, and the
            parent's overflow-hidden just clips everything with no scrollbar. */}
        <main className="min-w-0 flex-1 overflow-auto bg-gray-100 p-6">
          <Outlet />
        </main>
      </div>

      <ChangePasswordModal
        open={changePwOpen}
        onClose={() => setChangePwOpen(false)}
        onToast={onToast}
      />
    </div>
  );
}
